import { useMemo } from 'react';

import {
	type GenerateSearchUrlType,
	type NavigateCallback,
	type OnNavigate,
} from '../../../common/ui/app-context';
import { isOnSearchRoute, isSearchRouteUrl } from '../../../common/utils/url';
import { useWarmUpSearchPageQuery } from '../../../controllers/quick-find/warmup';

/**
 * Wraps the host-provided `onNavigate` so that a navigation toward the search
 * page first kicks off the search query warm-up to prime the backend
 * persisted-query cache.
 *
 * Behaviour notes:
 *
 * - When there is no `originalOnNavigate`, it is returned **by identity** so
 *   callers without a navigation handler are unaffected.
 * - Warm-up only fires when `isOnSearchRoute(generateSearchUrl)` returns `false`
 *   (i.e. we're definitively not already on the search page) AND the destination
 *   URL resolves to the search page route
 *   (`isSearchRouteUrl(url, generateSearchUrl) === true`). `true` (already on
 *   the search page) and `undefined` (route lookup failed) both skip warm-up,
 *   as do navigations whose destination is not the search page.
 */
export const useQuickFindOnNavigateWithWarmup = (
	originalOnNavigate: OnNavigate | undefined,
	generateSearchUrl: GenerateSearchUrlType | undefined,
): OnNavigate | undefined => {
	const warmUp = useWarmUpSearchPageQuery();

	return useMemo(() => {
		// Return original by identity when there is no navigation handler to wrap.
		if (!originalOnNavigate) {
			return originalOnNavigate;
		}

		const original = originalOnNavigate;
		const wrappedCallback: NavigateCallback = (url, action, event) => {
			// Only warm up when we definitively know we're NOT already on the
			// search page AND the destination is the search page itself. For
			// `isOnSearchRoute`, both `true` (already there) and `undefined`
			// (couldn't tell) skip warm-up.
			if (
				generateSearchUrl &&
				isOnSearchRoute(generateSearchUrl) === false &&
				isSearchRouteUrl(url, generateSearchUrl) === true
			) {
				warmUp(url);
			}

			return typeof original === 'function'
				? original(url, action, event)
				: original.callback(url, action, event);
		};

		return typeof original === 'function'
			? wrappedCallback
			: { callback: wrappedCallback, options: original.options };
	}, [originalOnNavigate, generateSearchUrl, warmUp]);
};
